import React, { useState, useEffect } from 'react';
import { Badge, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

const ResourceInsights = () => {
  const [insights, setInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInsights = async () => {
      const tenantID = localStorage.getItem('tenantID') || localStorage.getItem('organizationID');
      if (!tenantID) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `https://9cdf5nf6ya.execute-api.us-west-2.amazonaws.com/prod/insights?tenantID=${tenantID}`
        );
        
        if (!response.ok) throw new Error('Failed to fetch insights');
        
        const data = await response.json();
        setInsights(data.insights || []);
        setError(null);
      } catch (err) {
        console.error('Failed to load insights:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInsights();
  }, []);

  const getSeverityColor = (severity) => {
    const colors = { high: 'danger', medium: 'warning', low: 'info' };
    return colors[severity?.toLowerCase()] || 'secondary';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Security': '#e53e3e',
      'Reliability': '#3182ce',
      'Cost Optimization': '#38a169',
      'Operational Excellence': '#805ad5',
      'Performance Efficiency': '#d69e2e'
    };
    return colors[category] || '#718096';
  };

  return (
    <div
      className="rounded shadow"
      style={{
        backgroundColor: '#fff',
        borderRadius: '10px',
        height: '480px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ backgroundColor: '#6D5BFF', color: '#fff', padding: '15px', fontSize: '1.2rem', borderRadius: '10px 10px 0 0' }}>
        Cloud Advisor
      </div>
      
      <div className="flex-grow-1" style={{ overflowY: 'auto', padding: '16px' }}>
        {isLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '100%' }}>
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="text-muted mt-3 mb-1" style={{ fontWeight: '500' }}>Analyzing your infrastructure...</p>
            <small className="text-muted">This may take 15-30 seconds</small>
          </div>
        ) : error ? (
          <div className="p-3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}>
            <div className="text-danger fw-bold mb-2">⚠️ Unable to load recommendations</div>
            <small className="text-muted">{error}</small>
          </div>
        ) : insights.length === 0 ? (
          <div className="text-center text-muted py-5">No recommendations available</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {insights.map((insight, idx) => (
              <div 
                key={idx} 
                className="rounded p-3" 
                style={{ 
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <strong 
                    style={{ 
                      fontSize: '0.9rem', 
                      color: '#2d3748',
                      flex: 1,
                      marginRight: '8px',
                      lineHeight: '1.4',
                      wordBreak: 'break-word'
                    }}
                  >
                    {insight.title}
                  </strong>
                  <Badge 
                    bg={getSeverityColor(insight.severity)}
                    text="white"
                    style={{ 
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      padding: '4px 8px',
                      flexShrink: 0
                    }}
                  >
                    {insight.severity}
                  </Badge>
                </div>
                <div className="mb-2">
                  <span
                    style={{ 
                      fontSize: '0.7rem',
                      fontWeight: '500',
                      backgroundColor: getCategoryColor(insight.category),
                      color: '#fff',
                      padding: '3px 10px',
                      borderRadius: '12px',
                      display: 'inline-block',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {insight.category}
                  </span>
                </div>
                <p 
                  style={{ 
                    fontSize: '0.85rem', 
                    marginBottom: '10px',
                    color: '#4a5568',
                    lineHeight: '1.5',
                    wordBreak: 'break-word'
                  }}
                >
                  {insight.executiveSummary}
                </p>
                <div 
                  className="text-primary" 
                  style={{ 
                    fontSize: '0.75rem',
                    backgroundColor: '#ebf8ff',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    borderLeft: '3px solid #3182ce',
                    lineHeight: '1.4',
                    wordBreak: 'break-word'
                  }}
                >
                  <strong>Action:</strong> {insight.recommendation}
                </div>
                {insight.affectedResources && insight.affectedResources.length > 0 && (
                  <div 
                    style={{ 
                      fontSize: '0.75rem',
                      backgroundColor: '#fef5e7',
                      padding: '8px 10px',
                      borderRadius: '6px',
                      marginTop: '8px',
                      borderLeft: '3px solid #f39c12'
                    }}
                  >
                    <strong>Affected Resources ({insight.affectedResources.length}):</strong>
                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {insight.affectedResources.slice(0, 5).map((resource, ridx) => (
                        <code 
                          key={ridx}
                          style={{ 
                            fontSize: '0.7rem',
                            backgroundColor: '#fff',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            border: '1px solid #e2e8f0',
                            color: '#2d3748'
                          }}
                        >
                          {resource}
                        </code>
                      ))}
                      {insight.affectedResources.length > 5 && (
                        <span style={{ fontSize: '0.7rem', color: '#718096', marginLeft: '4px' }}>
                          +{insight.affectedResources.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceInsights;
